/**
 * CardRow — list item alternative to table rows (Control Hub style).
 *
 * Design system mapping (mch-card-row):
 *   • Container: grid auto-flow with thumb / title+subtitle / device-stack / meta cols / actions
 *   • Background: bg-white dark:bg-webex-gray-950
 *   • Border:  border webex-gray-300 dark:border-webex-gray-700 rounded-md p-3
 *   • Hover:   border webex-gray-400 dark:border-webex-gray-500 + shadow-overlay
 *
 * Composition:
 *   <CardRow.List>
 *     <CardRow>
 *       <CardRow.Select />
 *       <CardRow.Thumb><Icon /></CardRow.Thumb>
 *       <CardRow.Body title="Acme" subtitle="Desk · CLT" />
 *       <CardRow.Meta icon={<Icon />}>Hot Desking</CardRow.Meta>
 *       <CardRow.Actions><Button>...</Button></CardRow.Actions>
 *     </CardRow>
 *   </CardRow.List>
 */
import * as React from "react"
import { cn } from "@lib/cn"
import { Checkbox } from "./Checkbox"

function List({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("grid gap-3", className)} {...props} />
}

const Root = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-4 px-4 py-3",
        "bg-white dark:bg-webex-gray-950",
        "border border-webex-gray-300 dark:border-webex-gray-700 rounded-md",
        "transition-[border-color,box-shadow] duration-150 motion-reduce:transition-none",
        "hover:border-webex-gray-400 dark:hover:border-webex-gray-500",
        "hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] dark:hover:shadow-[0_4px_12px_rgba(0,0,0,0.6)]",
        className,
      )}
      {...props}
    />
  ),
)
Root.displayName = "CardRow"

function Select({ className, ...props }: React.ComponentProps<typeof Checkbox>) {
  return (
    <span className={cn("shrink-0", className)}>
      <Checkbox {...props} />
    </span>
  )
}

function Thumb({ className, children, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        "shrink-0 inline-grid place-items-center size-10 rounded-full",
        "bg-webex-gray-100 dark:bg-webex-gray-900",
        "[&>svg]:size-5 [&>img]:size-5 text-webex-gray-700 dark:text-webex-gray-300",
        className,
      )}
      {...props}
    >
      {children}
    </span>
  )
}

interface BodyProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title: React.ReactNode
  subtitle?: React.ReactNode
}

function Body({ className, title, subtitle, ...props }: BodyProps) {
  return (
    <div className={cn("flex-1 min-w-0", className)} {...props}>
      <div className="text-sm font-medium text-webex-gray-1000 dark:text-white truncate">{title}</div>
      {subtitle && (
        <div className="mt-0.5 text-xs text-webex-gray-600 dark:text-webex-gray-400 flex items-center gap-1.5 flex-wrap">
          {subtitle}
        </div>
      )}
    </div>
  )
}

interface MetaProps extends React.HTMLAttributes<HTMLSpanElement> {
  icon?: React.ReactNode
}

function Meta({ className, icon, children, ...props }: MetaProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 min-w-[8rem] text-sm text-webex-gray-1000 dark:text-white",
        "[&>svg]:size-3.5 [&>img]:size-3.5",
        className,
      )}
      {...props}
    >
      {icon && <span className="opacity-70">{icon}</span>}
      {children}
    </span>
  )
}

function Actions({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn("ml-auto inline-flex items-center gap-1", className)} {...props} />
}

function DeviceStack({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn("inline-flex items-center gap-1", className)} {...props} />
}

function DeviceThumb({ className, children, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        "inline-grid place-items-center size-9 rounded-md border",
        "bg-webex-gray-100 dark:bg-webex-gray-900",
        "border-webex-gray-300 dark:border-webex-gray-700",
        "[&>svg]:max-w-6 [&>svg]:max-h-6 [&>img]:max-w-6 [&>img]:max-h-6",
        "text-webex-gray-700 dark:text-webex-gray-300",
        className,
      )}
      {...props}
    >
      {children}
    </span>
  )
}

export const CardRow = Object.assign(Root, {
  List,
  Select,
  Thumb,
  Body,
  Meta,
  Actions,
  DeviceStack,
  DeviceThumb,
})
